import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/dropdown/dropdown';
import { useNavigate } from 'react-router';
import { useAuth } from 'providers/AuthProvider';
import { LogOut, Megaphone, Settings, UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FeedbackOverlay } from 'widgets/results/FeedbackOverlay';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { AmbossIcon, useAmboss } from 'widgets/amboss';
import { FailedTransmissionsIndicator } from 'widgets/failedTransmissions';
import { PatientSyncStatusIndicator } from 'widgets/patientSync';
import { PausedConsultationsIndicator } from 'widgets/pausedConsultation';
import { TitleBarLeading } from './TitleBarLeading';
import { WorkstationMenuItems } from './WorkstationMenuItems';
import {
  ActionsArea,
  AmbossButton,
  TitleBarNav,
  UserAvatar,
  UserAvatarFallback,
} from './TitleBar.styles';

export default function TitleBar({
  firstName,
  lastName,
  title,
  subtitle,
  dark = false,
  hasBackButton,
  interactionId,
  inert = false,
}: {
  firstName?: string;
  lastName?: string;
  title?: string;
  subtitle?: string;
  dark?: boolean;
  hasBackButton: boolean;
  interactionId?: string;
  inert?: boolean;
}) {
  const navigate = useNavigate();
  const avatarLabel = firstName ? firstName?.charAt(0) + lastName?.charAt(0) : 'U';
  const { logout } = useAuth();
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const amboss = useAmboss();
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const primaryTitle =
    title ?? (firstName ? t('welcome_back', { username: firstName }) : undefined);
  const logo = dark ? '/images/logo-white.svg' : '/images/logo-dark.svg';

  return (
    <TitleBarNav $dark={dark} inert={inert}>
      <TitleBarLeading
        title={primaryTitle}
        subtitle={subtitle}
        hasBackButton={hasBackButton}
        logoSrc={logo}
      />
      <ActionsArea>
        <PausedConsultationsIndicator />
        <FailedTransmissionsIndicator />
        <PatientSyncStatusIndicator />
        <AmbossButton
          onClick={() => {
            setIsDropdownOpen(false);
            amboss.open();
          }}
          aria-label={t('amboss.openTitle')}
          aria-expanded={amboss.isOpen}
        >
          <AmbossIcon size={20} />
        </AmbossButton>
        <FeedbackOverlay
          isVisible={showFeedbackDialog}
          setShowFeedbackDialog={setShowFeedbackDialog}
          interactionId={interactionId}
          triggerType="manual"
        />
        <DropdownMenu
          open={isDropdownOpen}
          onOpenChange={(open) => {
            setIsDropdownOpen(open);
            if (open) {
              trackEvent(ANALYTICS_EVENTS.TITLEBAR_MENU_OPENED, {
                hasBackButton,
                hasTitle: Boolean(title),
                hasInteractionId: Boolean(interactionId),
              });
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <UserAvatar aria-label={t('settings')} tabIndex={0}>
              <UserAvatarFallback $dark={dark}>{avatarLabel}</UserAvatarFallback>
            </UserAvatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel>{t('settings')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => {
                  trackEvent(ANALYTICS_EVENTS.TITLEBAR_MENU_ITEM_CLICKED, {
                    title: 'accountSettings',
                  });
                  void navigate('/profile');
                }}
              >
                <UserIcon /> {t('accountSettings')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  trackEvent(ANALYTICS_EVENTS.TITLEBAR_MENU_ITEM_CLICKED, {
                    title: 'feedback',
                  });
                  setShowFeedbackDialog(true);
                }}
              >
                <Megaphone /> {t('result.feedback.buttonTitle')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  trackEvent(ANALYTICS_EVENTS.TITLEBAR_MENU_ITEM_CLICKED, {
                    title: 'settings',
                  });
                  void navigate('/settings');
                }}
              >
                <Settings /> {t('settings')}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <WorkstationMenuItems />
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.TITLEBAR_MENU_ITEM_CLICKED, { title: 'logout' });
                void (async () => {
                  await logout();
                  toast.success(t('logout_successfully'));
                  void navigate('/?loggedOut=true');
                })();
              }}
            >
              <LogOut />
              {t('logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </ActionsArea>
    </TitleBarNav>
  );
}
